import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  Receipt, GraphUp, CashStack,
  ArrowClockwise, ExclamationCircle, BoxSeam,
} from "react-bootstrap-icons";
import { auth } from "../../../lib/config/firebaseInit";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "../../../utilities/toast";
import { goToLogin } from "../../../utilities/authRedirect";
import { formatCurrency } from "../../../utilities/formatCurrency";
import { useStoreMetrics, useActiveOrdersByStore } from "../../../lib/query/queries/useMerchantQueries";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Constants ────────────────────────────────────────────────────────────────

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif';

const STATUS_COLORS = {
  placed:     "#8E8E93",
  accepted:   "#007AFF",
  processing: "#FF9500",
  enroute:    "#5856D6",
  completed:  "#34C759",
  delivered:  "#34C759",
};

const DURATION_OPTIONS = [
  { name: "Week",  value: "week"  },
  { name: "Month", value: "month" },
  { name: "Year",  value: "year"  },
];

// Skeleton pulse animation — injected once
(() => {
  if (typeof document === "undefined") return;
  if (document.getElementById("store-dash-styles")) return;
  const el = document.createElement("style");
  el.id = "store-dash-styles";
  el.textContent = `
    @keyframes _sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._sk { animation: _sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
  `;
  document.head.appendChild(el);
})();

// ─── Auth hook (unchanged logic) ─────────────────────────────────────────────

const useAuth = () => {
  const [user,    setUser]    = useState(null);
  const [uid,     setUid]     = useState();
  const [token,   setToken]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        try {
          const result = await u.getIdTokenResult();
          if (result.claims.merchant) {
            setUid(u.uid);
            setUser(u);
            setToken(result.token);
            setError(false);
          } else {
            setError(true);
            setUid(null);
            setUser(null);
            setToken(null);
            toast.error("Access denied. Store role required.");
            goToLogin(navigate, location);
          }
        } catch (err) {
          console.error("Error checking user claims:", err);
          setError(true);
          setUid(null);
          setUser(null);
          setToken(null);
          goToLogin(navigate, location);
        }
      } else {
        setError(true);
        setUid(null);
        setUser(null);
        setToken(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return { user, uid, token, loading, error };
};

// ─── Atoms ────────────────────────────────────────────────────────────────────

function Skel({ width = "100%", height = 16, radius = 6, style = {} }) {
  return (
    <div
      className="_sk"
      style={{ width, height, borderRadius: radius, flexShrink: 0, ...style }}
    />
  );
}

function SectionCard({ title, action, children }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.07)",
        padding: "20px",
        fontFamily: FONT,
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          {title && (
            <p
              style={{
                margin: 0,
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: "#1C1C1E",
                letterSpacing: "-0.015em",
              }}
            >
              {title}
            </p>
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || "#8E8E93";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 8px",
        borderRadius: 7,
        fontSize: "0.6875rem",
        fontWeight: 600,
        color,
        background: color + "1A",
        whiteSpace: "nowrap",
        textTransform: "capitalize",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      {status}
    </span>
  );
}

function SegmentControl({ options, value, onChange }) {
  return (
    <div
      style={{
        display: "inline-flex",
        background: "rgba(118,118,128,0.12)",
        borderRadius: 10,
        padding: 2,
      }}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: "6px 16px",
            borderRadius: 8,
            border: "none",
            background: value === opt.value ? "#FFFFFF" : "transparent",
            color:
              value === opt.value ? "#1C1C1E" : "rgba(60,60,67,0.55)",
            fontSize: "0.8125rem",
            fontWeight: value === opt.value ? 600 : 400,
            cursor: "pointer",
            boxShadow:
              value === opt.value ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
            transition: "all 0.18s ease",
            letterSpacing: "-0.008em",
            fontFamily: FONT,
          }}
        >
          {opt.name}
        </button>
      ))}
    </div>
  );
}

// ─── Skeleton variants ────────────────────────────────────────────────────────

const SKEL_HEIGHTS = [80, 130, 60, 160, 95, 115, 75];

function MetricCardSkeleton() {
  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.07)",
        padding: "20px",
        height: "100%",
      }}
    >
      <Skel width={40} height={40} radius={10} />
      <Skel width="68%" height={11} radius={4} style={{ marginTop: 16, marginBottom: 8 }} />
      <Skel width="54%" height={34} radius={6} />
    </div>
  );
}

function BarChartSkeleton() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        height: 260,
        padding: "0 8px",
        gap: 8,
      }}
    >
      {SKEL_HEIGHTS.map((h, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Skel width="100%" height={h} radius={6} />
          <Skel width={28} height={10} radius={3} />
        </div>
      ))}
    </div>
  );
}

function PieChartSkeleton() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 20,
        height: 300,
        justifyContent: "center",
      }}
    >
      <Skel width={180} height={180} radius="50%" />
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
        {[80, 60, 70, 50].map((w, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Skel width={10} height={10} radius="50%" />
            <Skel width={w} height={10} radius={3} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Session error ────────────────────────────────────────────────────────────

function SessionErrorCard({ onLoginClick }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: "24px 16px",
        fontFamily: FONT,
        background: "#F2F2F7",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#FFFFFF",
          borderRadius: 20,
          border: "1px solid rgba(60,60,67,0.07)",
          padding: "40px 28px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 68,
            height: 68,
            borderRadius: "50%",
            background: "rgba(255,59,48,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <ExclamationCircle size={28} color="#FF3B30" />
        </div>
        <h2
          style={{
            margin: "0 0 10px",
            fontSize: "1.1875rem",
            fontWeight: 700,
            color: "#1C1C1E",
            letterSpacing: "-0.022em",
          }}
        >
          Session Expired
        </h2>
        <p
          style={{
            margin: "0 0 28px",
            fontSize: "0.875rem",
            color: "rgba(60,60,67,0.55)",
            lineHeight: 1.5,
          }}
        >
          Your session has expired. Please log in again to continue.
        </p>
        <button
          onClick={onLoginClick}
          style={{
            width: "100%",
            maxWidth: 240,
            padding: "13px",
            borderRadius: 14,
            border: "none",
            background: "#007AFF",
            color: "#fff",
            fontSize: "0.9375rem",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FONT,
          }}
        >
          Login Again
        </button>
      </div>
    </div>
  );
}

// ─── Metric card ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, Icon, accent, loading }) {
  if (loading) return <MetricCardSkeleton />;
  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: "1px solid rgba(60,60,67,0.07)",
        padding: "20px",
        height: "100%",
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: accent + "1A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={18} color={accent} />
      </div>
      <p
        style={{
          margin: "14px 0 5px",
          fontSize: "0.72rem",
          fontWeight: 600,
          color: "rgba(60,60,67,0.5)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: "1.875rem",
          fontWeight: 700,
          color: "#1C1C1E",
          letterSpacing: "-0.035em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </p>
    </div>
  );
}

// ─── Chart config ─────────────────────────────────────────────────────────────

const CHART_TOOLTIP = {
  contentStyle: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid rgba(60,60,67,0.1)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    fontFamily: FONT,
    fontSize: "0.8125rem",
  },
  labelStyle: { color: "#1C1C1E", fontWeight: 600 },
  itemStyle:  { color: "rgba(60,60,67,0.7)" },
};

const AXIS_TICK = {
  fill: "rgba(60,60,67,0.45)",
  fontSize: 12,
  fontFamily: FONT,
};

// ─── Empty chart placeholder ──────────────────────────────────────────────────

function EmptyChart({ message = "No data for this period" }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: "0.875rem",
        color: "rgba(60,60,67,0.4)",
        fontFamily: FONT,
        fontWeight: 500,
      }}
    >
      {message}
    </div>
  );
}

// ─── Active orders ────────────────────────────────────────────────────────────

function ActiveOrdersSection({ storeId, token }) {
  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useActiveOrdersByStore(storeId, token);

  const activeOrders = response?.activeOrders ?? [];
  const activeOrderCount = response?.activeOrderCount ?? 0;
  const error = isError ? "Failed to fetch active orders" : null;
  // isFetching-but-not-isLoading is a background refresh (button click) with
  // data already on screen — isLoading is the initial no-data-yet fetch.
  const isRefreshing = isFetching && !isLoading;

  const handleRefresh = () => refetch();

  const refreshBtn = (
    <button
      onClick={handleRefresh}
      disabled={isRefreshing || isLoading}
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        border: "none",
        background: "rgba(0,122,255,0.09)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isRefreshing || isLoading ? "not-allowed" : "pointer",
        opacity: isRefreshing || isLoading ? 0.5 : 1,
        transition: "opacity 0.15s ease",
        flexShrink: 0,
      }}
    >
      {isRefreshing ? (
        <Spinner
          animation="border"
          style={{ width: 14, height: 14, borderWidth: 2, color: "#007AFF" }}
        />
      ) : (
        <ArrowClockwise size={16} color="#007AFF" />
      )}
    </button>
  );

  return (
    <SectionCard
      title={
        <span>
          Active Orders
          {activeOrderCount > 0 && (
            <span
              style={{
                marginLeft: 8,
                fontSize: "0.8125rem",
                color: "#007AFF",
                fontWeight: 600,
              }}
            >
              ({activeOrderCount})
            </span>
          )}
        </span>
      }
      action={refreshBtn}
    >
      <p
        style={{
          margin: "-8px 0 16px",
          fontSize: "0.8125rem",
          color: "rgba(60,60,67,0.5)",
        }}
      >
        Orders you are currently handling
      </p>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: "11px 14px",
            background: "rgba(255,59,48,0.07)",
            borderRadius: 10,
            border: "1px solid rgba(255,59,48,0.18)",
            marginBottom: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "0.875rem",
            color: "#FF3B30",
            fontFamily: FONT,
          }}
        >
          <span>{error}</span>
          <button
            onClick={refetch}
            style={{
              background: "none",
              border: "none",
              color: "#FF3B30",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: "0.875rem",
              padding: 0,
              fontFamily: FONT,
            }}
          >
            Try again
          </button>
        </div>
      )}

      {/* Skeleton */}
      {isLoading ? (
        <Row className="g-3">
          {[1, 2, 3, 4].map((i) => (
            <Col xs={12} md={6} key={i}>
              <div
                style={{
                  background: "#FFFFFF",
                  borderRadius: 14,
                  border: "1px solid rgba(60,60,67,0.07)",
                  borderLeft: "3px solid rgba(60,60,67,0.1)",
                  padding: "14px 16px",
                }}
              >
                <Skel width="55%" height={14} radius={4} style={{ marginBottom: 8 }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Skel width="45%" height={14} radius={4} />
                  <Skel width={64} height={22} radius={7} />
                </div>
                <Skel width="38%" height={11} radius={3} style={{ marginTop: 10 }} />
              </div>
            </Col>
          ))}
        </Row>
      ) : activeOrders.length > 0 ? (
        /* Order cards grid */
        <Row className="g-3">
          {activeOrders.map((order) => (
            <Col xs={12} md={6} key={order.id}>
              <div
                style={{
                  background: "#FFFFFF",
                  borderRadius: 14,
                  border: "1px solid rgba(60,60,67,0.07)",
                  borderLeft: `3px solid ${STATUS_COLORS[order.status] ?? "#8E8E93"}`,
                  padding: "14px 16px",
                  fontFamily: FONT,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 8,
                  }}
                >
                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#1C1C1E",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {order.firstName} {order.lastName}
                    </p>
                    <p
                      style={{
                        margin: "3px 0 0",
                        fontSize: "0.75rem",
                        color: "rgba(60,60,67,0.5)",
                      }}
                    >
                      {order.phone}
                    </p>
                  </div>
                  <StatusPill status={order.status} />
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.72rem",
                    color: "rgba(60,60,67,0.38)",
                  }}
                >
                  Updated {order.updatedAt}
                </p>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        /* Empty state */
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            textAlign: "center",
            fontFamily: FONT,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "rgba(60,60,67,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 14,
            }}
          >
            <BoxSeam size={24} color="rgba(60,60,67,0.35)" />
          </div>
          <p
            style={{
              margin: "0 0 4px",
              fontSize: "0.9375rem",
              fontWeight: 600,
              color: "#1C1C1E",
              letterSpacing: "-0.015em",
            }}
          >
            No active orders
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "0.8125rem",
              color: "rgba(60,60,67,0.5)",
            }}
          >
            Orders you accept will appear here
          </p>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

const Main = () => {
  const { uid, token, loading: authLoading, error: authError } = useAuth();
  const [duration, setDuration] = useState("week");
  const navigate = useNavigate();
  const location = useLocation();

  const storeId = uid;

  // One useQueries fan-out for the five metrics (mirrors the original
  // Promise.all) — each metric gets its own cache entry keyed on
  // [storeId, duration], so flipping back to a previously-viewed period is
  // instant instead of re-fetching.
  const {
    ordersCount: numOrders,
    ordersAverage: avgOrderValue,
    revenue: totalRevenue,
    revenuePeriod,
    statusAverage: statusPeriod,
    isLoading: loading,
    isError,
  } = useStoreMetrics(storeId, duration, token);

  const error = isError ? "Failed to load dashboard data." : null;

  const revenueData = useMemo(() => {
    const rd = revenuePeriod?.revenueData;
    if (!rd) return [];
    if (duration === "week") {
      const dayOrder = ["MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY","SUNDAY"];
      return dayOrder.map((d) => ({
        label: d.charAt(0) + d.slice(1).toLowerCase(),
        revenue: rd[d] || 0,
      }));
    }
    if (duration === "month") {
      return Object.keys(rd).map(Number).sort((a, b) => a - b).map((day) => ({
        label: `Day ${day}`,
        revenue: rd[day] || 0,
      }));
    }
    if (duration === "year") {
      const monthOrder = ["JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE","JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"];
      return monthOrder.map((m) => ({
        label: m.charAt(0) + m.slice(1).toLowerCase(),
        revenue: rd[m] || 0,
      }));
    }
    return [];
  }, [revenuePeriod, duration]);

  const statusData = useMemo(() => {
    if (!statusPeriod?.statusCounts) return [];
    return Object.entries(statusPeriod.statusCounts).map(([status, count]) => ({
      status,
      count: count || 0,
    }));
  }, [statusPeriod]);

  if (authLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#F2F2F7",
        }}
      >
        <Spinner
          animation="border"
          style={{ width: 28, height: 28, borderWidth: 2, color: "#007AFF" }}
        >
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </div>
    );
  }

  if (authError) {
    return <SessionErrorCard onLoginClick={() => goToLogin(navigate, location)} />;
  }

  const durationLabel =
    duration.charAt(0).toUpperCase() + duration.slice(1);

  return (
    <div
      style={{
        fontFamily: FONT,
        background: "#F2F2F7",
        minHeight: "100vh",
        padding: "20px 16px 48px",
      }}
    >
      <Seo {...generateNoIndexSEO("Merchant Dashboard")} />
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>

        {/* ── Page header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: 700,
                color: "#1C1C1E",
                letterSpacing: "-0.025em",
              }}
            >
              Sales Dashboard
            </h1>
            <p
              style={{
                margin: "3px 0 0",
                fontSize: "0.8125rem",
                color: "rgba(60,60,67,0.5)",
              }}
            >
              Overview for the current {duration}
            </p>
          </div>
          <SegmentControl
            options={DURATION_OPTIONS}
            value={duration}
            onChange={setDuration}
          />
        </div>

        {/* ── Metric cards ── */}
        <Row className="g-3 mb-3">
          <Col xs={12} md={4}>
            <MetricCard
              label="Total Orders"
              value={numOrders?.orderCount ?? 0}
              Icon={Receipt}
              accent="#007AFF"
              loading={loading}
            />
          </Col>
          <Col xs={12} md={4}>
            <MetricCard
              label="Average Order Value"
              value={formatCurrency(avgOrderValue?.averageOrderValue ?? 0)}
              Icon={GraphUp}
              accent="#5856D6"
              loading={loading}
            />
          </Col>
          <Col xs={12} md={4}>
            <MetricCard
              label="Total Revenue"
              value={formatCurrency(totalRevenue?.totalRevenue ?? 0)}
              Icon={CashStack}
              accent="#34C759"
              loading={loading}
            />
          </Col>
        </Row>

        {/* ── Revenue chart ── */}
        <div style={{ marginBottom: 12 }}>
          <SectionCard title={`Revenue · ${durationLabel}`}>
            <div style={{ width: "100%", height: 280 }}>
              {loading ? (
                <BarChartSkeleton />
              ) : revenueData.every((d) => d.revenue === 0) ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={revenueData}
                    margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#007AFF" stopOpacity={0.14} />
                        <stop offset="95%" stopColor="#007AFF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(60,60,67,0.06)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      tick={AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => formatCurrency(v)}
                      tick={AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      width={76}
                    />
                    <Tooltip
                      {...CHART_TOOLTIP}
                      formatter={(v) => [formatCurrency(v), "Revenue"]}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{
                        fontFamily: FONT,
                        fontSize: "0.8125rem",
                        color: "rgba(60,60,67,0.6)",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#007AFF"
                      strokeWidth={2}
                      fill="url(#revenueGrad)"
                      dot={{ r: 3, fill: "#007AFF", strokeWidth: 0 }}
                      activeDot={{ r: 5, fill: "#007AFF", strokeWidth: 0 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </SectionCard>
        </div>

        {/* ── Order status chart ── */}
        <div style={{ marginBottom: 12 }}>
          <SectionCard title={`Order Status · ${durationLabel}`}>
            <div style={{ width: "100%", height: 300 }}>
              {loading ? (
                <PieChartSkeleton />
              ) : statusData.every((d) => d.count === 0) ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={110}
                      innerRadius={54}
                      labelLine={false}
                      paddingAngle={2}
                    >
                      {statusData.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={STATUS_COLORS[entry.status] || "#8E8E93"}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      {...CHART_TOOLTIP}
                      formatter={(v) => [`${v} orders`, ""]}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      formatter={(value) =>
                        value.charAt(0).toUpperCase() + value.slice(1)
                      }
                      wrapperStyle={{
                        fontFamily: FONT,
                        fontSize: "0.8125rem",
                        color: "rgba(60,60,67,0.6)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </SectionCard>
        </div>

        {/* ── Active orders ── */}
        <ActiveOrdersSection storeId={storeId} token={token} />

        {/* ── Data error banner ── */}
        {error && (
          <div
            style={{
              marginTop: 12,
              padding: "11px 14px",
              background: "rgba(255,59,48,0.07)",
              borderRadius: 10,
              border: "1px solid rgba(255,59,48,0.18)",
              fontSize: "0.875rem",
              color: "#FF3B30",
              fontFamily: FONT,
            }}
          >
            {error}
          </div>
        )}

      </div>
    </div>
  );
};

export default Main;
